import './FuelSurchargeTable.less';

import { ClockCircleOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Popover,
  Select,
  Spin,
  Switch,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';

import { useSurchargeHistory } from '../../hooks/apiHooks';
import { Carrier } from '../../models/Carrier';
import { Option } from '../../models/Option';
import type { Valuehistory } from '../../models/Valuehistory';
import FuelSurchargeBulkUpdateModal, {
  FuelSurchargeBulkUpdateValues,
} from '../modals/FuelSurchargeBulkUpdateModal';
import CPTable from './CPTable';

export type FuelSurchargeTableRow = Carrier & {
  option: Option | null;
  optionLoading?: boolean;
};

export type FuelSurchargeSaveValues = {
  carrierId: number;
  fscExpiryEmailEnabled: boolean;
  fuelSurcharge: number;
  fuelSurchargeExpiry: string | null;
};

type FuelSurchargeDraft = {
  fscExpiryEmailEnabled: boolean;
  fuelSurcharge: number | string | null;
  fuelSurchargeExpiry: Dayjs | null;
};

export type FuelSurchargeBulkSaveResult = {
  succeededIds: number[];
};

interface IFuelSurchargeTableProps {
  loading?: boolean;
  onBulkSave?: (
    values: FuelSurchargeSaveValues[]
  ) => void | Promise<FuelSurchargeBulkSaveResult | void>;
  onSave?: (values: FuelSurchargeSaveValues) => void | Promise<unknown>;
  rows: FuelSurchargeTableRow[];
  savingCarrierId?: number;
}

const hasFuelValue = (surcharge: unknown) => parseFloat(String(surcharge)) > 0;

const formatExpiry = (value: Dayjs | string | null | undefined) => {
  if (!value) return '';
  const parsed = dayjs.isDayjs(value) ? value : dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const toDraft = (option: Option | null | undefined): FuelSurchargeDraft => {
  const expiry = option?.fuelSurchargeExpiry
    ? dayjs(moment(option.fuelSurchargeExpiry).format('YYYY-MM-DD'))
    : null;
  return {
    fscExpiryEmailEnabled: !!option?.fscExpiryEmailEnabled,
    fuelSurcharge: option?.fuelSurcharge ?? 0,
    fuelSurchargeExpiry: expiry?.isValid() ? expiry : null,
  };
};

const isDraftDirty = (draft: FuelSurchargeDraft, saved: FuelSurchargeDraft) =>
  (parseFloat(String(draft.fuelSurcharge)) || 0) !==
    (parseFloat(String(saved.fuelSurcharge)) || 0) ||
  formatExpiry(draft.fuelSurchargeExpiry) !==
    formatExpiry(saved.fuelSurchargeExpiry) ||
  !!draft.fscExpiryEmailEnabled !== !!saved.fscExpiryEmailEnabled;

const isDraftValid = (draft: FuelSurchargeDraft) =>
  !hasFuelValue(draft.fuelSurcharge) ||
  !!formatExpiry(draft.fuelSurchargeExpiry);

const disablePastAndToday = (date: Dayjs | null) =>
  !!date && date.valueOf() < moment().add(1, 'days').startOf('day').valueOf();

const getLatestCreatedAt = (history: Valuehistory[] | null | undefined) => {
  if (!history?.length) return undefined;
  return history.reduce<string | undefined>((latest, item) => {
    if (!item.createdAt) return latest;
    if (!latest) return item.createdAt;
    return moment(item.createdAt).isAfter(latest) ? item.createdAt : latest;
  }, undefined);
};

const getDisplayDate = (
  history: Valuehistory[] | null | undefined,
  lastSurchargeChange?: string
) => {
  const historyDate = getLatestCreatedAt(history);
  if (historyDate && lastSurchargeChange) {
    return moment(historyDate).isAfter(lastSurchargeChange)
      ? historyDate
      : lastSurchargeChange;
  }
  return historyDate || lastSurchargeChange;
};

const LastUpdatedHistoryCell: React.FC<{
  carrierId: number;
  lastSurchargeChange?: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}> = ({ carrierId, lastSurchargeChange, onOpenChange, open }) => {
  const { data: history, isError } = useSurchargeHistory(carrierId, open);
  const displayDate = getDisplayDate(history, lastSurchargeChange);
  const sortedHistory = [...(history ?? [])].sort(
    (a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
  );

  let content: React.ReactNode;
  if (isError) {
    content = (
      <div className="fuel-surcharge-table__surcharge-history-empty">
        Failed to load history
      </div>
    );
  } else if (history === undefined) {
    content = (
      <div className="fuel-surcharge-table__surcharge-history-empty">
        <Spin size="small" />
      </div>
    );
  } else if (!sortedHistory.length) {
    content = (
      <div className="fuel-surcharge-table__surcharge-history-empty">
        No surcharge history
      </div>
    );
  } else {
    content = (
      <div className="fuel-surcharge-table__surcharge-history-scroll">
        <table className="fuel-surcharge-table__surcharge-history-table">
          <thead>
            <tr>
              <th>Value</th>
              <th>Date</th>
              <th>User</th>
            </tr>
          </thead>
          <tbody>
            {sortedHistory.map((surcharge) => (
              <tr key={surcharge.id}>
                <td>{surcharge.value}</td>
                <td>
                  {moment(surcharge.createdAt).format('YYYY-MM-DD hh:mm A')}
                </td>
                <td>
                  {surcharge.User?.firstName} {surcharge.User?.lastName}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const showClock =
    (!!displayDate && history === undefined) ||
    sortedHistory.length > 0 ||
    isError;

  return (
    <span className="fuel-surcharge-table__last-updated">
      {showClock ? (
        <Popover
          content={content}
          open={open}
          title="Surcharge History"
          trigger="click"
          onOpenChange={onOpenChange}
        >
          <Button
            aria-label="Surcharge history"
            htmlType="button"
            icon={<ClockCircleOutlined />}
            shape="circle"
            size="small"
            onClick={(event) => event.stopPropagation()}
          />
        </Popover>
      ) : null}
      <span>
        {displayDate ? (
          moment(displayDate).format('YYYY-MM-DD')
        ) : (
          <>&mdash;&mdash;&mdash;&mdash;</>
        )}
      </span>
    </span>
  );
};

type FuelExpiryTone = 'red' | 'yellow' | 'blue';
type ExpiryFilter = 'all' | FuelExpiryTone;

const EXPIRY_FILTER_OPTIONS: { label: string; value: ExpiryFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Expired', value: 'red' },
  { label: 'Expired today or past 3 days', value: 'yellow' },
  { label: 'Expiring soon', value: 'blue' },
];

const getExpiryTone = (expiryStr: string): FuelExpiryTone | undefined => {
  const expiry = moment(expiryStr).startOf('day');
  if (!expiry.isValid()) return undefined;
  const daysSince = moment().startOf('day').diff(expiry, 'days');
  if (daysSince > 7) return 'red';
  if (daysSince >= 0) return 'yellow';
  if (daysSince >= -3) return 'blue';
  return undefined;
};

const FuelSurchargeTable: React.FC<IFuelSurchargeTableProps> = ({
  loading,
  onBulkSave,
  onSave,
  rows,
  savingCarrierId,
}) => {
  const [nameFilter, setNameFilter] = useState('');
  const [expiryFilter, setExpiryFilter] = useState<ExpiryFilter>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [drafts, setDrafts] = useState<Record<number, FuelSurchargeDraft>>({});
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [openHistoryCarrierId, setOpenHistoryCarrierId] = useState<
    number | null
  >(null);

  const optionByCarrierId = useMemo(() => {
    const map = new Map<number, Option | null>();
    rows.forEach((row) => map.set(row.id, row.option));
    return map;
  }, [rows]);

  const getEffectiveDraft = useCallback(
    (carrierId: number) =>
      drafts[carrierId] ?? toDraft(optionByCarrierId.get(carrierId)),
    [drafts, optionByCarrierId]
  );

  const updateDraft = useCallback(
    (carrierId: number, patch: Partial<FuelSurchargeDraft>) => {
      setDrafts((prev) => {
        const current =
          prev[carrierId] ?? toDraft(optionByCarrierId.get(carrierId));
        let next = { ...current, ...patch };
        if ('fuelSurcharge' in patch && !hasFuelValue(next.fuelSurcharge)) {
          next = {
            ...next,
            fscExpiryEmailEnabled: false,
            fuelSurchargeExpiry: null,
          };
        }
        return { ...prev, [carrierId]: next };
      });
    },
    [optionByCarrierId]
  );

  const handleSave = useCallback(
    async (record: FuelSurchargeTableRow) => {
      const draft = getEffectiveDraft(record.id);
      if (
        !isDraftDirty(draft, toDraft(record.option)) ||
        !isDraftValid(draft)
      ) {
        return;
      }
      const hasFuel = hasFuelValue(draft.fuelSurcharge);
      try {
        await onSave?.({
          carrierId: record.id,
          fscExpiryEmailEnabled: hasFuel
            ? !!draft.fscExpiryEmailEnabled
            : false,
          fuelSurcharge: parseFloat(String(draft.fuelSurcharge)) || 0,
          fuelSurchargeExpiry: hasFuel
            ? formatExpiry(draft.fuelSurchargeExpiry) || null
            : null,
        });
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[record.id];
          return next;
        });
      } catch {}
    },
    [getEffectiveDraft, onSave]
  );

  const handleBulkSubmit = useCallback(
    async ({
      fuelSurcharge,
      fuelSurchargeExpiry,
    }: FuelSurchargeBulkUpdateValues) => {
      const hasFuel = hasFuelValue(fuelSurcharge);
      const payloads: FuelSurchargeSaveValues[] = selectedRowKeys.flatMap(
        (key) => {
          const carrierId = Number(key);
          const row = rows.find((item) => item.id === carrierId);
          if (!row || row.optionLoading) return [];
          return [
            {
              carrierId,
              fscExpiryEmailEnabled: hasFuel
                ? !!row.option?.fscExpiryEmailEnabled
                : false,
              fuelSurcharge,
              fuelSurchargeExpiry: hasFuel ? fuelSurchargeExpiry : null,
            },
          ];
        }
      );
      if (!payloads.length) return;
      setIsBulkSaving(true);
      try {
        const result = await onBulkSave?.(payloads);
        const succeededIds = new Set(
          result?.succeededIds ?? payloads.map((item) => item.carrierId)
        );
        setDrafts((prev) => {
          const next = { ...prev };
          succeededIds.forEach((id) => {
            delete next[id];
          });
          return next;
        });
        setSelectedRowKeys((prev) =>
          prev.filter((key) => !succeededIds.has(Number(key)))
        );
        setIsBulkUpdateOpen(false);
      } finally {
        setIsBulkSaving(false);
      }
    },
    [onBulkSave, rows, selectedRowKeys]
  );

  const filteredRows = useMemo(() => {
    const search = nameFilter.trim().toLowerCase();
    return rows.filter((row) => {
      if (search && !(row.name || '').toLowerCase().includes(search)) {
        return false;
      }
      if (expiryFilter === 'all') return true;
      const expiryStr = formatExpiry(
        getEffectiveDraft(row.id).fuelSurchargeExpiry
      );
      return !!expiryStr && getExpiryTone(expiryStr) === expiryFilter;
    });
  }, [expiryFilter, getEffectiveDraft, nameFilter, rows]);

  const columns: ColumnsType<FuelSurchargeTableRow> = useMemo(
    () => [
      {
        className: 'fuel-surcharge-table__carrier',
        dataIndex: 'name',
        title: 'Carrier',
      },
      {
        dataIndex: 'fuelSurcharge',
        render: (_value, record) => {
          if (record.optionLoading) return <Spin size="small" />;
          const draft = getEffectiveDraft(record.id);
          return (
            <span
              style={{ alignItems: 'center', display: 'inline-flex', gap: 4 }}
            >
              <InputNumber
                className="fuel-surcharge-table__fuel-input"
                controls={false}
                disabled={isBulkSaving || savingCarrierId === record.id}
                value={
                  draft.fuelSurcharge == null || draft.fuelSurcharge === ''
                    ? null
                    : Number(draft.fuelSurcharge)
                }
                onChange={(value) =>
                  updateDraft(record.id, { fuelSurcharge: value })
                }
              />
              %
            </span>
          );
        },
        title: 'Fuel surcharge %',
        width: 160,
      },
      {
        dataIndex: 'fuelSurchargeExpiry',
        render: (_value, record) => {
          if (record.optionLoading) return <Spin size="small" />;
          const draft = getEffectiveDraft(record.id);
          const hasFuel = hasFuelValue(draft.fuelSurcharge);
          const expiryStr = formatExpiry(draft.fuelSurchargeExpiry);
          const expiryTone = expiryStr ? getExpiryTone(expiryStr) : undefined;
          const needsExpiry = hasFuel && !expiryStr;
          return (
            <DatePicker
              allowClear={false}
              className={
                expiryTone
                  ? `fuel-surcharge-table__expiry--${expiryTone}`
                  : undefined
              }
              disabled={
                !hasFuel || isBulkSaving || savingCarrierId === record.id
              }
              disabledDate={disablePastAndToday}
              status={needsExpiry ? 'error' : undefined}
              value={draft.fuelSurchargeExpiry}
              onChange={(value) =>
                updateDraft(record.id, { fuelSurchargeExpiry: value })
              }
            />
          );
        },
        title: 'Expiry',
        width: 180,
      },
      {
        dataIndex: 'fscExpiryEmailEnabled',
        render: (_value, record) => {
          if (record.optionLoading) return <Spin size="small" />;
          const draft = getEffectiveDraft(record.id);
          const hasFuel = hasFuelValue(draft.fuelSurcharge);
          if (!hasFuel) return null;
          return (
            <Switch
              checked={!!draft.fscExpiryEmailEnabled}
              checkedChildren="On"
              disabled={isBulkSaving || savingCarrierId === record.id}
              unCheckedChildren="Off"
              onChange={(checked) =>
                updateDraft(record.id, { fscExpiryEmailEnabled: checked })
              }
            />
          );
        },
        title: 'Email on expiry',
        width: 160,
      },
      {
        dataIndex: 'last_surcharge_change',
        render: (_value, record) => (
          <LastUpdatedHistoryCell
            carrierId={record.id}
            lastSurchargeChange={record.last_surcharge_change}
            open={openHistoryCarrierId === record.id}
            onOpenChange={(nextOpen) => {
              setOpenHistoryCarrierId((current) => {
                if (nextOpen) return record.id;
                return current === record.id ? null : current;
              });
            }}
          />
        ),
        title: 'Last updated',
        width: 160,
      },
      {
        key: 'save',
        render: (_value, record) => {
          const draft = getEffectiveDraft(record.id);
          const dirty = isDraftDirty(draft, toDraft(record.option));
          const valid = isDraftValid(draft);
          const isSaving = isBulkSaving || savingCarrierId === record.id;
          return (
            <Button
              className="fuel-surcharge-table__save"
              disabled={
                !dirty || !valid || record.optionLoading || isBulkSaving
              }
              loading={isSaving && !isBulkSaving}
              type="primary"
              onClick={() => handleSave(record)}
            >
              Save
            </Button>
          );
        },
        title: '',
        width: 110,
      },
    ],
    [
      getEffectiveDraft,
      handleSave,
      isBulkSaving,
      openHistoryCarrierId,
      savingCarrierId,
      updateDraft,
    ]
  );

  const selectedCount = selectedRowKeys.length;

  return (
    <div className="fuel-surcharge-table-wrap">
      <div className="fuel-surcharge-table-toolbar">
        <div className="fuel-surcharge-table-toolbar__start">
          <Input
            allowClear
            className="fuel-surcharge-table-toolbar__search"
            placeholder="Search by carrier name"
            prefix={<SearchOutlined />}
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
          <Select
            className="fuel-surcharge-table-toolbar__filter"
            options={EXPIRY_FILTER_OPTIONS}
            value={expiryFilter}
            onChange={setExpiryFilter}
          />
          <span className="fuel-surcharge-table-toolbar__legend">
            <span className="fuel-surcharge-table-toolbar__legend-item">
              <span className="fuel-surcharge-table-toolbar__legend-circle fuel-surcharge-table-toolbar__legend-circle--red" />
              Expired
            </span>
            <span className="fuel-surcharge-table-toolbar__legend-item">
              <span className="fuel-surcharge-table-toolbar__legend-circle fuel-surcharge-table-toolbar__legend-circle--yellow" />
              Expired today or past 3 days
            </span>
            <span className="fuel-surcharge-table-toolbar__legend-item">
              <span className="fuel-surcharge-table-toolbar__legend-circle fuel-surcharge-table-toolbar__legend-circle--blue" />
              Expiring soon
            </span>
          </span>
        </div>
        {selectedCount > 0 ? (
          <Button
            disabled={isBulkSaving}
            type="primary"
            onClick={() => setIsBulkUpdateOpen(true)}
          >
            Update selected ({selectedCount})
          </Button>
        ) : null}
      </div>
      <CPTable
        className="cp-app-list fuel-surcharge-table"
        columns={columns}
        dataSource={filteredRows}
        loading={loading}
        pagination={false}
        rowKey={(row) => row.id}
        rowSelection={{
          getCheckboxProps: (record) => ({
            disabled:
              !!record.optionLoading || isBulkSaving || isBulkUpdateOpen,
          }),
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
          selectedRowKeys,
          type: 'checkbox',
        }}
        sticky={{ offsetHeader: 106 }}
      />
      {isBulkUpdateOpen ? (
        <FuelSurchargeBulkUpdateModal
          selectedCount={selectedCount}
          submitting={isBulkSaving}
          onClose={() => setIsBulkUpdateOpen(false)}
          onSubmit={handleBulkSubmit}
        />
      ) : null}
    </div>
  );
};

export default FuelSurchargeTable;
